import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { environment } from 'environments/environment';

declare const Redoc: {
    init: (
        specOrSpecUrl: any,
        options?: Record<string, any>,
        element?: HTMLElement,
        callback?: () => void
    ) => void;
};

interface DocChild {
    id: string;
    label: string;
    description: string;
}

interface DocSection {
    id: string;
    label: string;
    icon: string;
    description: string;
    children: DocChild[];
}

export interface StatusMappingItem {
    status_index: number;
    event_code: string;
    shipment_status: string;
    amazon_event_code: string;
    amazon_shipment_status: string;
    city: string;
    state: string;
    countrycode: string;
}

export interface MarketplaceFlowDoc {
    flowCode: string;
    flowName: string;
    sourceCountry: string;
    destinationCountry: string;
    trackingPrefix: string;
    city: string;
    state: string;
    statuses: StatusMappingItem[];
}

@Component({
    selector: 'app-devloper-document',
    templateUrl: './devloper-document.html',
    styleUrls: ['./devloper-document.scss']
})
export class DevloperDocumentComponent implements OnInit, AfterViewInit {
    @ViewChild('redocContainer') redocContainer?: ElementRef<HTMLElement>;
    @ViewChild('customerRedocContainer') customerRedocContainer?: ElementRef<HTMLElement>;

    url = '';
    openApiSpec: any = null;
    specLoading = false;
    specLoadError = '';

    customerOpenApiSpec: any = null;
    customerSpecLoading = false;
    customerSpecLoadError = '';

    // Active Top-Level API Mode: 'amz' or 'customer'
    activeApiMode: 'amz' | 'customer' = 'amz';

    // Existing Amazon API Sections (Preserved Intact)
    sections: DocSection[] = [
        {
            id: 'introduction',
            label: 'Introduction',
            icon: 'fa fa-book',
            description: 'Amazon API overview, basic authentication, and throttling limits.',
            children: [
                {
                    id: 'overview',
                    label: 'Overview',
                    description: 'General introduction to Lemonmode Amazon API documentation.'
                },
                {
                    id: 'authentication',
                    label: 'Authentication',
                    description: 'Basic authentication format required for Amazon API requests.'
                },
                {
                    id: 'throttling',
                    label: 'Throttling Limit',
                    description: 'Request-per-minute limits for Amazon API usage.'
                }
            ]
        },
        {
            id: 'apis',
            label: 'AMZ APIs',
            icon: 'fa fa-code',
            description: 'Amazon tracking endpoints and status references.',
            children: [
                {
                    id: 'tracking-details',
                    label: 'Get Tracking Details',
                    description: 'Endpoint details for tracking by tracking number or order number.'
                },
                {
                    id: 'tracking-status',
                    label: 'Tracking Status Description',
                    description: 'Amazon status codes and descriptions.'
                }
            ]
        },
        {
            id: 'languages',
            label: 'Programming Languages',
            icon: 'fa fa-laptop-code',
            description: 'Sample integration snippets in multiple languages.',
            children: [
                { id: 'php', label: 'PHP', description: 'Sample request in PHP.' },
                { id: 'java', label: 'Java', description: 'Sample request in Java.' },
                { id: 'python', label: 'Python', description: 'Sample request in Python.' },
                { id: 'curl', label: 'CURL', description: 'Sample request in CURL.' },
                { id: 'http', label: 'HTTP', description: 'Raw HTTP request example.' },
                { id: 'csharp', label: 'C#', description: 'Sample request in C#.' }
            ]
        }
    ];

    // Customer API Sections
    customerSections: DocSection[] = [
        {
            id: 'customer-intro',
            label: 'Overview & Auth',
            icon: 'fa fa-shield-halved',
            description: 'Customer API overview, M2M OAuth, and HMAC-SHA256 signing.',
            children: [
                { id: 'overview-auth', label: 'Overview & Architecture', description: 'Customer Tracking API architecture and customer isolation.' },
                { id: 'oauth-flow', label: 'M2M OAuth Tokens', description: 'Client Credentials OAuth token & refresh token endpoints.' },
                // { id: 'hmac-signing', label: 'HMAC-SHA256 Signing', description: 'Canonical string computation and required HMAC HTTP headers.' },
                { id: 'credential-mgmt', label: 'Credentials Management', description: 'Issue, rotate, revoke, and check credential status.' }
            ]
        },
        {
            id: 'customer-endpoints',
            label: 'Customer Apis',
            icon: 'fa fa-code',
            description: 'Redoc interactive OpenAPI 3.0 documentation for Customer Tracking APIs.',
            children: [
                { id: 'customer-redoc-view', label: 'API Specification', description: 'Interactive OpenAPI specification for Customer Tracking APIs.' }
            ]
        },
        // {
        //     id: 'marketplace-flow-sec',
        //     label: 'Marketplace Statuses',
        //     icon: 'fa fa-route',
        //     description: 'Dynamic Marketplace Flow status mappings & event codes.',
        //     children: [
        //         { id: 'flow-status-viewer', label: 'Dynamic Flow Status Viewer', description: 'Interactive status lifecycle viewer by Marketplace Flow.' }
        //     ]
        // }
    ];

    selectedSection = 'introduction';
    selectedChild = 'overview';
    expandedSection = 'introduction';

    selectedCustomerSection = 'customer-intro';
    selectedCustomerChild = 'overview-auth';
    expandedCustomerSection = 'customer-intro';

    // Dynamic Marketplace Flows Dataset
    selectedFlowCode = 'US-UAE';
    marketplaceFlows: MarketplaceFlowDoc[] = [
        {
            flowCode: 'US-UAE',
            flowName: 'US to UAE Marketplace Flow',
            sourceCountry: 'US',
            destinationCountry: 'UAE',
            trackingPrefix: '971',
            city: 'NEWYORK',
            state: 'NEWYORK',
            statuses: [
                { status_index: 0, event_code: 'LOC', shipment_status: 'Order Placed', amazon_event_code: 'LOC', amazon_shipment_status: 'Order Placed', city: 'NEWYORK', state: 'NEWYORK', countrycode: 'US' },
                { status_index: 1, event_code: 'LITU', shipment_status: 'Order Confirmed', amazon_event_code: 'LITU', amazon_shipment_status: 'Order Confirmed', city: 'NEWYORK', state: 'NEWYORK', countrycode: 'US' },
                { status_index: 2, event_code: 'LRU', shipment_status: 'In Transit to USA Hub', amazon_event_code: 'LRU', amazon_shipment_status: 'SHIPMENT ARRIVED AT HUB', city: 'NEWYORK', state: 'NEWYORK', countrycode: 'US' },
                { status_index: 3, event_code: 'LAE', shipment_status: 'Received at USA Hub', amazon_event_code: 'LAE', amazon_shipment_status: 'Arrived in HUB', city: 'NEWYORK', state: 'NEWYORK', countrycode: 'US' },
                { status_index: 4, event_code: 'II', shipment_status: 'In Transit to UAE', amazon_event_code: 'II', amazon_shipment_status: 'In Transit to UAE', city: 'NEWYORK', state: 'NEWYORK', countrycode: 'US' },
                { status_index: 9, event_code: 'LAI', shipment_status: 'Arrived in UAE', amazon_event_code: 'LAI', amazon_shipment_status: 'Arrived in UAE', city: 'DUBAI', state: 'DUBAI', countrycode: 'AE' },
                { status_index: 10, event_code: 'LUC', shipment_status: 'Under Custom Clearance', amazon_event_code: 'LUC', amazon_shipment_status: 'Under Custom Clearance', city: 'DUBAI', state: 'DUBAI', countrycode: 'AE' },
                { status_index: 11, event_code: 'OT', shipment_status: 'Dispatched from UAE Hub', amazon_event_code: 'OT', amazon_shipment_status: 'Dispatched from UAE Hub', city: 'DUBAI', state: 'DUBAI', countrycode: 'AE' },
                { status_index: 12, event_code: 'OO', shipment_status: 'Order Out for Delivery', amazon_event_code: 'OO', amazon_shipment_status: 'Order Out for Delivery', city: 'DUBAI', state: 'DUBAI', countrycode: 'AE' },
                { status_index: 13, event_code: 'DL', shipment_status: 'Order Delivered', amazon_event_code: 'DL', amazon_shipment_status: 'Order Delivered', city: 'DUBAI', state: 'DUBAI', countrycode: 'AE' }
            ]
        },
        {
            flowCode: 'US-IN',
            flowName: 'US to India Marketplace Flow',
            sourceCountry: 'US',
            destinationCountry: 'IN',
            trackingPrefix: '91',
            city: 'NEWYORK',
            state: 'NEWYORK',
            statuses: [
                { status_index: 0, event_code: 'LOC', shipment_status: 'Order Placed', amazon_event_code: 'LOC', amazon_shipment_status: 'Order Placed', city: 'NEWYORK', state: 'NEWYORK', countrycode: 'US' },
                { status_index: 1, event_code: 'LITU', shipment_status: 'Order Confirmed', amazon_event_code: 'LITU', amazon_shipment_status: 'Order Confirmed', city: 'NEWYORK', state: 'NEWYORK', countrycode: 'US' },
                { status_index: 2, event_code: 'LRU', shipment_status: 'In Transit to USA Hub', amazon_event_code: 'LRU', amazon_shipment_status: 'SHIPMENT ARRIVED AT HUB', city: 'NEWYORK', state: 'NEWYORK', countrycode: 'US' },
                { status_index: 4, event_code: 'LAI', shipment_status: 'Received at USA Hub', amazon_event_code: 'LAI', amazon_shipment_status: 'Arrived in India', city: 'NEWYORK', state: 'NEWYORK', countrycode: 'US' },
                { status_index: 5, event_code: 'II', shipment_status: 'In Transit to India', amazon_event_code: 'II', amazon_shipment_status: 'In Transit to India', city: 'NEWYORK', state: 'NEWYORK', countrycode: 'US' },
                { status_index: 9, event_code: 'LAI', shipment_status: 'Arrived in India', amazon_event_code: 'DL', amazon_shipment_status: 'Arrived in India', city: 'MUMBAI', state: 'MAHARASHTRA', countrycode: 'IN' },
                { status_index: 10, event_code: 'LUC', shipment_status: 'Under Custom Clearance', amazon_event_code: 'OO', amazon_shipment_status: 'Under Custom Clearance', city: 'MUMBAI', state: 'MAHARASHTRA', countrycode: 'IN' },
                { status_index: 11, event_code: 'OT', shipment_status: 'Dispatched from Mumbai Hub', amazon_event_code: 'OT', amazon_shipment_status: 'Dispatched from Mumbai Hub', city: 'MUMBAI', state: 'MAHARASHTRA', countrycode: 'IN' },
                { status_index: 12, event_code: 'OO', shipment_status: 'Order Out for Delivery', amazon_event_code: 'OO', amazon_shipment_status: 'Order Out for Delivery', city: 'MUMBAI', state: 'MAHARASHTRA', countrycode: 'IN' },
                { status_index: 13, event_code: 'DL', shipment_status: 'Order Delivered', amazon_event_code: 'DL', amazon_shipment_status: 'Order Delivered', city: 'MUMBAI', state: 'MAHARASHTRA', countrycode: 'IN' }
            ]
        },
        {
            flowCode: 'IN-US',
            flowName: 'India to US Marketplace Flow',
            sourceCountry: 'IN',
            destinationCountry: 'US',
            trackingPrefix: '91',
            city: 'MUMBAI',
            state: 'MAHARASHTRA',
            statuses: [
                { status_index: 0, event_code: 'LOC', shipment_status: 'Order Placed', amazon_event_code: 'LOC', amazon_shipment_status: 'Order Placed', city: 'MUMBAI', state: 'MAHARASHTRA', countrycode: 'IN' },
                { status_index: 1, event_code: 'LITU', shipment_status: 'Order Manifested', amazon_event_code: 'LITU', amazon_shipment_status: 'Order Manifested', city: 'MUMBAI', state: 'MAHARASHTRA', countrycode: 'IN' },
                { status_index: 2, event_code: 'LRU', shipment_status: 'Arrived at India Hub', amazon_event_code: 'LRU', amazon_shipment_status: 'Arrived at Hub', city: 'MUMBAI', state: 'MAHARASHTRA', countrycode: 'IN' },
                { status_index: 5, event_code: 'LTI', shipment_status: 'In Transit to USA', amazon_event_code: 'LTI', amazon_shipment_status: 'In Transit to USA', city: 'MUMBAI', state: 'MAHARASHTRA', countrycode: 'IN' },
                { status_index: 9, event_code: 'LAE', shipment_status: 'Arrived in USA', amazon_event_code: 'LAE', amazon_shipment_status: 'Arrived in USA', city: 'NEWYORK', state: 'NEWYORK', countrycode: 'US' },
                { status_index: 10, event_code: 'LUC', shipment_status: 'US Customs Cleared', amazon_event_code: 'LUC', amazon_shipment_status: 'Customs Cleared', city: 'NEWYORK', state: 'NEWYORK', countrycode: 'US' },
                { status_index: 11, event_code: 'OT', shipment_status: 'Out for Last-Mile Delivery', amazon_event_code: 'OT', amazon_shipment_status: 'Out for Delivery', city: 'NEWYORK', state: 'NEWYORK', countrycode: 'US' },
                { status_index: 13, event_code: 'DL', shipment_status: 'Delivered to Customer', amazon_event_code: 'DL', amazon_shipment_status: 'Delivered', city: 'NEWYORK', state: 'NEWYORK', countrycode: 'US' }
            ]
        },
        {
            flowCode: 'UAE-IN',
            flowName: 'UAE to India Marketplace Flow',
            sourceCountry: 'UAE',
            destinationCountry: 'IN',
            trackingPrefix: '971',
            city: 'DUBAI',
            state: 'DUBAI',
            statuses: [
                { status_index: 0, event_code: 'LOC', shipment_status: 'Order Placed', amazon_event_code: 'LOC', amazon_shipment_status: 'Order Placed', city: 'DUBAI', state: 'DUBAI', countrycode: 'AE' },
                { status_index: 2, event_code: 'LRU', shipment_status: 'Arrived at UAE Hub', amazon_event_code: 'LRU', amazon_shipment_status: 'Arrived at Hub', city: 'DUBAI', state: 'DUBAI', countrycode: 'AE' },
                { status_index: 5, event_code: 'II', shipment_status: 'In Transit to India', amazon_event_code: 'II', amazon_shipment_status: 'In Transit to India', city: 'DUBAI', state: 'DUBAI', countrycode: 'AE' },
                { status_index: 9, event_code: 'LAI', shipment_status: 'Arrived in India', amazon_event_code: 'LAI', amazon_shipment_status: 'Arrived in India', city: 'MUMBAI', state: 'MAHARASHTRA', countrycode: 'IN' },
                { status_index: 11, event_code: 'OT', shipment_status: 'Dispatched from Mumbai Hub', amazon_event_code: 'OT', amazon_shipment_status: 'Dispatched from Mumbai Hub', city: 'MUMBAI', state: 'MAHARASHTRA', countrycode: 'IN' },
                { status_index: 13, event_code: 'DL', shipment_status: 'Order Delivered', amazon_event_code: 'DL', amazon_shipment_status: 'Order Delivered', city: 'MUMBAI', state: 'MAHARASHTRA', countrycode: 'IN' }
            ]
        }
    ];

    private readonly specUrl = 'assets/lemonmode-tracking-openapi.json';
    private readonly customerSpecUrl = 'assets/lemonmode-customer-tracking-openapi.json';
    private readonly redocOptions = {
        hideDownloadButton: false,
        pathInMiddlePanel: true,
        requiredPropsFirst: true,
        sortPropsAlphabetically: true
    };

    constructor(private http: HttpClient) { }

    ngOnInit(): void {
        this.url = environment.domain_name;
    }

    ngAfterViewInit(): void {
        this.renderRedocIfNeeded();
        this.renderCustomerRedocIfNeeded();
    }

    setApiMode(mode: 'amz' | 'customer'): void {
        this.activeApiMode = mode;
        if (mode === 'amz') {
            this.renderRedocIfNeeded();
        } else {
            this.renderCustomerRedocIfNeeded();
        }
    }

    toggleSection(sectionId: string): void {
        this.expandedSection = this.expandedSection === sectionId ? '' : sectionId;
    }

    toggleCustomerSection(sectionId: string): void {
        this.expandedCustomerSection = this.expandedCustomerSection === sectionId ? '' : sectionId;
    }

    selectSection(sectionId: string, childId?: string): void {
        this.selectedSection = sectionId;
        this.expandedSection = sectionId;

        if (childId) {
            this.selectedChild = childId;
            this.renderRedocIfNeeded();
            return;
        }

        const section = this.sections.find(item => item.id === sectionId);
        this.selectedChild = section?.children?.[0]?.id || '';
        this.renderRedocIfNeeded();
    }

    selectCustomerSection(sectionId: string, childId?: string): void {
        this.selectedCustomerSection = sectionId;
        this.expandedCustomerSection = sectionId;

        if (childId) {
            this.selectedCustomerChild = childId;
            this.renderCustomerRedocIfNeeded();
            return;
        }

        const section = this.customerSections.find(item => item.id === sectionId);
        this.selectedCustomerChild = section?.children?.[0]?.id || '';
        this.renderCustomerRedocIfNeeded();
    }

    get selectedSectionLabelWithChild(): string {
        const section = this.sections.find(item => item.id === this.selectedSection);
        const child = section?.children.find(item => item.id === this.selectedChild);
        return child ? `${section?.label} / ${child.label}` : section?.label || 'Amazon API Documentation';
    }

    get selectedSectionDescription(): string {
        const section = this.sections.find(item => item.id === this.selectedSection);
        const child = section?.children.find(item => item.id === this.selectedChild);
        return child?.description || section?.description || 'Amazon API documentation details';
    }

    get selectedCustomerSectionLabelWithChild(): string {
        const section = this.customerSections.find(item => item.id === this.selectedCustomerSection);
        const child = section?.children.find(item => item.id === this.selectedCustomerChild);
        return child ? `${section?.label} / ${child.label}` : section?.label || 'Customer Tracking API Documentation';
    }

    get selectedCustomerSectionDescription(): string {
        const section = this.customerSections.find(item => item.id === this.selectedCustomerSection);
        const child = section?.children.find(item => item.id === this.selectedCustomerChild);
        return child?.description || section?.description || 'Customer API documentation details';
    }

    get currentFlow(): MarketplaceFlowDoc {
        return this.marketplaceFlows.find(f => f.flowCode === this.selectedFlowCode) || this.marketplaceFlows[0];
    }

    private renderRedocIfNeeded(): void {
        if (this.activeApiMode !== 'amz' || this.selectedSection !== 'apis' || this.selectedChild !== 'tracking-details') {
            return;
        }

        if (this.openApiSpec) {
            setTimeout(() => this.renderRedoc());
            return;
        }

        this.loadOpenApiSpec();
    }

    private loadOpenApiSpec(): void {
        if (this.specLoading) {
            return;
        }

        this.specLoading = true;
        this.specLoadError = '';

        this.http.get(this.specUrl).subscribe({
            next: (spec: any) => {
                this.openApiSpec = spec;
                this.specLoading = false;
                setTimeout(() => this.renderRedoc());
            },
            error: () => {
                this.specLoadError = `OpenAPI file not found at ${this.specUrl}.`;
                this.specLoading = false;
            }
        });
    }

    private renderRedoc(): void {
        if (!this.openApiSpec || !this.redocContainer?.nativeElement || typeof Redoc === 'undefined') {
            return;
        }

        this.redocContainer.nativeElement.innerHTML = '';
        Redoc.init(this.openApiSpec, this.redocOptions, this.redocContainer.nativeElement);
    }

    private renderCustomerRedocIfNeeded(): void {
        if (this.activeApiMode !== 'customer' || this.selectedCustomerSection !== 'customer-endpoints' || this.selectedCustomerChild !== 'customer-redoc-view') {
            return;
        }

        if (this.customerOpenApiSpec) {
            setTimeout(() => this.renderCustomerRedoc());
            return;
        }

        this.loadCustomerOpenApiSpec();
    }

    private loadCustomerOpenApiSpec(): void {
        if (this.customerSpecLoading) {
            return;
        }

        this.customerSpecLoading = true;
        this.customerSpecLoadError = '';

        this.http.get(this.customerSpecUrl).subscribe({
            next: (spec: any) => {
                this.customerOpenApiSpec = spec;
                this.customerSpecLoading = false;
                setTimeout(() => this.renderCustomerRedoc());
            },
            error: () => {
                this.customerSpecLoadError = `Customer OpenAPI file not found at ${this.customerSpecUrl}.`;
                this.customerSpecLoading = false;
            }
        });
    }

    private renderCustomerRedoc(): void {
        if (!this.customerOpenApiSpec || !this.customerRedocContainer?.nativeElement || typeof Redoc === 'undefined') {
            return;
        }

        this.customerRedocContainer.nativeElement.innerHTML = '';
        Redoc.init(this.customerOpenApiSpec, this.redocOptions, this.customerRedocContainer.nativeElement);
    }

    copyData(el: HTMLElement): void {
        const codeContent = el.textContent || '';
        const textarea = document.createElement('textarea');
        textarea.value = codeContent;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
    }

    downloadCustomerApiPdf(): void {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Please allow popups to download the Customer API Documentation PDF.');
            return;
        }

        const flowRows = this.currentFlow.statuses.map(s => `
            <tr>
                <td>${s.status_index}</td>
                <td><code>${s.event_code}</code></td>
                <td><strong>${s.shipment_status}</strong></td>
                <td><code>${s.amazon_event_code || '-'}</code></td>
                <td>${s.amazon_shipment_status}</td>
                <td>${s.city}, ${s.state} (${s.countrycode})</td>
            </tr>
        `).join('');

        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Customer Tracking API Documentation</title>
                <style>
                    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 30px; color: #1e293b; line-height: 1.6; }
                    h1 { color: #0f172a; border-bottom: 3px solid #0284c7; padding-bottom: 10px; }
                    h2 { color: #0369a1; margin-top: 30px; border-bottom: 1px solid #cbd5e1; padding-bottom: 6px; }
                    h3 { color: #0f172a; margin-top: 20px; }
                    .badge { background: #e0f2fe; color: #0369a1; font-weight: bold; padding: 4px 10px; border-radius: 6px; font-size: 12px; }
                    .method-post { background: #dcfce7; color: #15803d; padding: 4px 8px; border-radius: 4px; font-weight: bold; }
                    .method-put { background: #fef9c3; color: #a16207; padding: 4px 8px; border-radius: 4px; font-weight: bold; }
                    .method-get { background: #e0f2fe; color: #0369a1; padding: 4px 8px; border-radius: 4px; font-weight: bold; }
                    code, pre { background: #f1f5f9; font-family: 'Courier New', monospace; border-radius: 6px; font-size: 13px; }
                    pre { padding: 14px; overflow-x: auto; background: #0f172a; color: #f8fafc; }
                    table { width: 100%; border-collapse: collapse; margin-top: 12px; margin-bottom: 20px; }
                    th, td { border: 1px solid #cbd5e1; padding: 10px 12px; text-align: left; font-size: 13px; }
                    th { background: #f8fafc; color: #0f172a; }
                    .header-box { background: #f8fafc; border: 1px solid #cbd5e1; padding: 16px; border-radius: 8px; margin-bottom: 20px; }
                </style>
            </head>
            <body>
                <h1>Customer Tracking API Documentation</h1>
                <p><strong>Lemonmode Customer Machine-to-Machine (M2M) API Specification</strong></p>
                <div class="header-box">
                    <p><strong>Base URL:</strong> <code>https://api.lemonmode.com/v1</code></p>
                    <p><strong>Authentication:</strong> Bearer Token + HMAC-SHA256 Signature</p>
                    <p><strong>Field Naming Standard:</strong> Strictly <strong>camelCase</strong> Request & Response payloads</p>
                </div>

                <h2>1. Required HTTP Headers & HMAC-SHA256 Signing</h2>
                <p>Every protected Customer Tracking API request requires the following HTTP headers:</p>
                <table>
                    <thead>
                        <tr><th>Header Name</th><th>Description / Format</th><th>Example</th></tr>
                    </thead>
                    <tbody>
                        <tr><td><code>Authorization</code></td><td>Bearer OAuth access token</td><td><code>Bearer &lt;ACCESS_TOKEN&gt;</code></td></tr>
                        <tr><td><code>X-Client-ID</code></td><td>Your Customer API Client ID</td><td><code>LEMONMODE_ABC123</code></td></tr>
                        <tr><td><code>X-Timestamp</code></td><td>ISO 8601 UTC timestamp</td><td><code>2026-08-17T13:00:00.000Z</code></td></tr>
                        <tr><td><code>X-Request-ID</code></td><td>Unique request ID (Replay protection)</td><td><code>req-1700000000000</code></td></tr>
                        <tr><td><code>X-Signature</code></td><td>HMAC-SHA256 Hex signature</td><td><code>a8f9c1023...</code></td></tr>
                    </tbody>
                </table>
                <p><strong>Canonical String Format:</strong> <code>HTTP_METHOD + REQUEST_PATH + TIMESTAMP + REQUEST_BODY</code></p>

                <h2>2. Per-Customer Rate Limiting & API Usage Tracking</h2>
                <p>Every API call is evaluated against per-customer per-endpoint rate limits (10 requests per 60 seconds allowed). Responses include <code>X-RateLimit-Limit</code>, <code>X-RateLimit-Remaining</code>, and <code>X-RateLimit-Reset</code> HTTP headers.</p>

                <h2>3. Customer API Endpoints</h2>

                <h3><span class="method-post">POST</span> /v1/tracking/generate-tracking-number</h3>
                <p><strong>Purpose:</strong> Generates a unique tracking number and stores complete customer order & item details.</p>
                <p><strong>Mandatory Fields:</strong> <code>orderNumber</code>, <code>orderItemId</code></p>
                <p><strong>Order Prefix Rule:</strong> Automatically prepends the last 4 characters of customer ID (e.g., <code>9011-1001</code>).</p>
                <p><strong>Request Example (camelCase):</strong></p>
                <pre>{
  "orderNumber": "1001",
  "orderItemId": "ITEM1001",
  "quantity": 1,
  "buyerName": "John Doe",
  "city": "New York",
  "state": "NY",
  "zipCode": "10001",
  "phoneNumber": "+1234567890",
  "storeName": "MyStore",
  "flowCode": "US-UAE"
}</pre>
                <p><strong>Success Response (200 OK):</strong></p>
                <pre>{
  "status": 200,
  "message": "Tracking number generated successfully",
  "data": {
    "trackingNumber": "TRK9718492015",
    "orderNumber": "9011-1001",
    "orderItemId": "ITEM1001",
    "flowCode": "US-UAE",
    "storeName": "MyStore",
    "createdAt": "2026-08-17T13:00:00.000Z"
  }
}</pre>

                <h3><span class="method-put">PUT</span> /v1/tracking/update-last-mile</h3>
                <p><strong>Purpose:</strong> Updates carrier tracking details for an existing order item.</p>
                <p><strong>Mandatory Fields:</strong> <code>trackingNumber</code>, <code>carrierName</code>, <code>carrierTrackingNumber</code></p>
                <p><strong>Request Example (camelCase):</strong></p>
                <pre>{
  "trackingNumber": "TRK9718492015",
  "carrierName": "Blue Dart",
  "carrierTrackingNumber": "BD123456789"
}</pre>
                <p><strong>Success Response (200 OK):</strong></p>
                <pre>{
  "status": 200,
  "message": "Last-mile tracking updated successfully",
  "data": {
    "trackingNumber": "TRK9718492015",
    "carrierName": "Blue Dart",
    "carrierTrackingNumber": "BD123456789",
    "orderNumber": "9011-1001",
    "orderItemId": "ITEM1001"
  }
}</pre>

                <h3><span class="method-post">POST</span> /v1/tracking/last-mile-history</h3>
                <p><strong>Purpose:</strong> Adds last-mile tracking history status events for an order.</p>
                <p><strong>Mandatory Fields:</strong> <code>trackingNumber</code> (or <code>orderNumber</code>), <code>trackingHistory</code> array (or single event object)</p>
                <p><strong>Request Example:</strong></p>
                <pre>{
  "trackingNumber": "TRK9718492015",
  "trackingHistory": [
    {
      "status": "In Transit",
      "activity": "In Transit",
      "location": "India Export Hub",
      "city": "Mumbai",
      "state": "Maharashtra",
      "countrycode": "IN",
      "date": "2026-08-17T12:00:00.000Z"
    },
    {
      "status": "Out for Delivery",
      "activity": "Out for Delivery",
      "location": "Dubai Hub",
      "city": "Dubai",
      "state": "Dubai",
      "countrycode": "AE",
      "date": "2026-08-17T13:00:00.000Z"
    }
  ]
}</pre>
                <p><strong>Success Response (200 OK):</strong></p>
                <pre>{
  "status": 200,
  "message": "Last-mile tracking history processed successfully",
  "data": {
    "trackingNumber": "TRK9718492015",
    "orderNumber": "9011-1001",
    "orderItemId": "ITEM1001",
    "carrierName": "Blue Dart",
    "carrierTrackingNumber": "BD123456789",
    "isDelivered": false,
    "lastStatus": {
      "status": "Out for Delivery",
      "activity": "Out for Delivery",
      "location": "Dubai Hub",
      "city": "Dubai",
      "state": "Dubai",
      "countrycode": "AE",
      "date": "2026-08-17T13:00:00.000Z",
      "time": "13:00",
      "eventCode": "",
      "remarks": "",
      "isDelivered": false
    },
    "processedEventsCount": 2,
    "newEventsInsertedCount": 2,
    "trackingHistory": [ ... ]
  }
}</pre>

                <h3><span class="method-get">GET</span> /v1/tracking/:tracking_number</h3>
                <p><strong>Purpose:</strong> Retrieves shipment status timeline logs and carrier tracking details.</p>
                <p><strong>Success Response (200 OK):</strong></p>
                <pre>{
  "status": 200,
  "message": "Tracking details retrieved successfully",
  "data": {
    "trackingNumber": "TRK9718492015",
    "orderNumber": "9011-1001",
    "status": 200,
    "trackingData": [
      {
        "status_index": 0,
        "event_code": "LOC",
        "status": "Order Placed",
        "city": "NEWYORK",
        "state": "NEWYORK",
        "date": "2026-08-17T13:00:00.000Z"
      }
    ]
  }
}</pre>

                <h2>4. Dynamic Marketplace Flow Status Lifecycle (${this.currentFlow.flowCode})</h2>
                <p><strong>Flow:</strong> ${this.currentFlow.flowName} (${this.currentFlow.sourceCountry} → ${this.currentFlow.destinationCountry})</p>
                <table>
                    <thead>
                        <tr><th>Index</th><th>Event Code</th><th>Shipment Status</th><th>Amazon Event</th><th>Amazon Shipment Status</th><th>Location</th></tr>
                    </thead>
                    <tbody>
                        ${flowRows}
                    </tbody>
                </table>

                <h2>5. Error Responses</h2>
                <table>
                    <thead><tr><th>HTTP Code</th><th>Error Message</th><th>Description</th></tr></thead>
                    <tbody>
                        <tr><td><code>400 Bad Request</code></td><td>orderNumber is required / Invalid carrier name</td><td>Mandatory input validation failed.</td></tr>
                        <tr><td><code>401 Unauthorized</code></td><td>Invalid Client Secret / HMAC Signature Mismatch</td><td>HMAC signature or credentials invalid.</td></tr>
                        <tr><td><code>403 Forbidden</code></td><td>Your account is pending activation. Please contact admin.</td><td>Customer account is pending admin review (status: 0).</td></tr>
                        <tr><td><code>404 Not Found</code></td><td>Tracking number not found</td><td>Resource does not exist.</td></tr>
                        <tr><td><code>409 Conflict</code></td><td>Replay attack detected / Request ID already processed</td><td>Duplicate X-Request-ID within 5-minute window.</td></tr>
                        <tr><td><code>429 Too Many Requests</code></td><td>Per-customer rate limit exceeded (10 requests per 60s allowed)</td><td>Per-customer endpoint rate limit exceeded.</td></tr>
                    </tbody>
                </table>

                <script>
                    window.onload = function() {
                        window.print();
                    };
                </script>
            </body>
            </html>
        `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();
    }
}

